import React, { useState, useEffect } from 'react';
import { Shield, Link, MessageCircle, Video, CheckCircle, Clock, XCircle } from 'lucide-react';

const API = 'https://api.naijapredict.net';
const T = { bg2:'#0d1114', border:'rgba(255,255,255,.06)', muted:'#4a5a63', green:'#00c566', yellow:'#f5c842', red:'#ff4444' };

export default function Verification({ user }) {
  const [status, setStatus]     = useState(null); // null, pending, approved, rejected
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm]         = useState({ handles: '', description: '', niche: '' });

  useEffect(() => { checkStatus(); }, []);

  async function checkStatus() {
    try {
      const token = localStorage.getItem('np_creator_token');
      const res = await fetch(API+'/api/creator/verification', {
        headers: { Authorization: 'Bearer '+token }
      });
      const data = await res.json();
      setStatus(data.status || null);
      if (data.form) setForm(data.form);
    } catch(e) {}
    finally { setLoading(false); }
  }

  async function submit() {
    if (!form.instagram && !form.twitterx && !form.youtube) return alert('Please enter at least one social media handle');
    if (!form.niche.trim()) return alert('Please enter your content niche');
    setSubmitting(true);
    try {
      const token = localStorage.getItem('np_creator_token');
      const res = await fetch(API+'/api/creator/verification', {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:'Bearer '+token },
        body: JSON.stringify({ niche: form.niche, audience: form.audience, description: form.description, instagram: form.instagram||'', twitterx: form.twitterx||'', youtube: form.youtube||'', telegram: form.telegram||'', facebook: form.facebook||'' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStatus('pending');
    } catch(e) { alert('Error: '+e.message); }
    finally { setSubmitting(false); }
  }

  if (loading) return <div style={{textAlign:'center',padding:40,color:T.muted}}>Loading...</div>;

  // Already approved
  if (status === 'approved') return (
    <div style={{maxWidth:480}}>
      <div style={{background:'rgba(0,197,102,.06)',border:'1px solid rgba(0,197,102,.2)',borderRadius:16,padding:24,textAlign:'center'}}>
        <CheckCircle size={40} color={T.green} style={{marginBottom:12}}/>
        <div style={{fontSize:20,fontWeight:800,color:T.green,marginBottom:6}}>Verified Creator ✅</div>
        <div style={{fontSize:13,color:T.muted}}>Your account is verified. You have full access to all creator features.</div>
      </div>
    </div>
  );

  // Pending review
  if (status === 'pending') return (
    <div style={{maxWidth:480}}>
      <div style={{background:'rgba(245,200,66,.06)',border:'1px solid rgba(245,200,66,.2)',borderRadius:16,padding:24,textAlign:'center'}}>
        <Clock size={40} color={T.yellow} style={{marginBottom:12}}/>
        <div style={{fontSize:20,fontWeight:800,color:T.yellow,marginBottom:6}}>Under Review</div>
        <div style={{fontSize:13,color:T.muted}}>Your application is being reviewed. We'll notify you via Telegram within 24-48 hours.</div>
      </div>
    </div>
  );

  // Rejected
  if (status === 'rejected') return (
    <div style={{maxWidth:480}}>
      <div style={{background:'rgba(255,68,68,.06)',border:'1px solid rgba(255,68,68,.2)',borderRadius:16,padding:24,textAlign:'center',marginBottom:20}}>
        <XCircle size={40} color={T.red} style={{marginBottom:12}}/>
        <div style={{fontSize:20,fontWeight:800,color:T.red,marginBottom:6}}>Application Rejected</div>
        <div style={{fontSize:13,color:T.muted}}>Your application was not approved. You may reapply with updated information.</div>
      </div>
      <button onClick={()=>setStatus(null)} style={{width:'100%',padding:12,borderRadius:12,border:'none',background:'rgba(255,255,255,.06)',color:'#e8eaeb',fontSize:14,cursor:'pointer',fontFamily:'inherit'}}>
        Reapply
      </button>
    </div>
  );

  // Application form
  return (
    <div style={{maxWidth:480}}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:6}}>
        <Shield size={24} color={T.green}/>
        <h1 style={{fontSize:22,fontWeight:800}}>Creator Verification</h1>
      </div>
      <p style={{fontSize:13,color:T.muted,marginBottom:24}}>
        Submit your social media handles for review. Once approved you can create markets and earn royalties.
      </p>

      {/* Niche */}
      <div style={{marginBottom:16}}>
        <label style={{display:'block',fontSize:11,color:T.muted,fontWeight:600,letterSpacing:.5,marginBottom:6}}>CONTENT NICHE *</label>
        <select value={form.niche} onChange={e=>setForm({...form,niche:e.target.value})}
          style={{width:'100%',padding:'12px 14px',background:'rgba(255,255,255,.04)',border:`1px solid ${T.border}`,borderRadius:10,color:form.niche?'#e8eaeb':T.muted,fontSize:14,outline:'none',fontFamily:'inherit'}}>
          <option value="">Select your niche...</option>
          {['Sports','Politics','Entertainment','Crypto/Finance','News & Current Affairs','Music','Nollywood','BBNaija','Tech','Lifestyle','Other'].map(n=>(
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>

      {/* Social handles */}
      <div style={{marginBottom:16}}>
        <label style={{display:'block',fontSize:11,color:T.muted,fontWeight:600,letterSpacing:.5,marginBottom:6}}>SOCIAL MEDIA HANDLES *</label>
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {[
            ['instagram', Link,          'Instagram',      '@yourhandle'],
            ['twitterx',  MessageCircle, 'Twitter/X',      '@yourhandle'],
            ['youtube',   Video,         'YouTube',        'Channel name or URL'],
            ['telegram',  MessageCircle, 'Telegram',       '@yourusername or channel link'],
            ['facebook',  Link,          'Facebook Page',  'Page name or URL'],
          ].map(([key, Icon, platform, placeholder])=>(
            <div key={key} style={{display:'flex',alignItems:'center',gap:10,background:'rgba(255,255,255,.04)',border:`1px solid ${T.border}`,borderRadius:10,padding:'10px 14px'}}>
              <Icon size={16} color={T.muted}/>
              <span style={{fontSize:12,color:T.muted,width:90,flexShrink:0}}>{platform}</span>
              <input
                value={form[key] || ''}
                onChange={e=>setForm({...form,[key]:e.target.value})}
                placeholder={placeholder}
                style={{flex:1,background:'transparent',border:'none',color:'#e8eaeb',fontSize:13,outline:'none',fontFamily:'inherit'}}/>
            </div>
          ))}
        </div>
      </div>

      {/* Audience size */}
      <div style={{marginBottom:16}}>
        <label style={{display:'block',fontSize:11,color:T.muted,fontWeight:600,letterSpacing:.5,marginBottom:6}}>TOTAL AUDIENCE SIZE *</label>
        <select value={form.audience||''} onChange={e=>setForm({...form,audience:e.target.value})}
          style={{width:'100%',padding:'12px 14px',background:'rgba(255,255,255,.04)',border:`1px solid ${T.border}`,borderRadius:10,color:form.audience?'#e8eaeb':T.muted,fontSize:14,outline:'none',fontFamily:'inherit'}}>
          <option value="">Select range...</option>
          {['Under 1,000','1,000 - 5,000','5,000 - 10,000','10,000 - 50,000','50,000 - 100,000','100,000+'].map(r=>(
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Description */}
      <div style={{marginBottom:20}}>
        <label style={{display:'block',fontSize:11,color:T.muted,fontWeight:600,letterSpacing:.5,marginBottom:6}}>BRIEF DESCRIPTION</label>
        <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})}
          placeholder="Tell us about your content and audience..."
          rows={3}
          style={{width:'100%',padding:'12px 14px',background:'rgba(255,255,255,.04)',border:`1px solid ${T.border}`,borderRadius:10,color:'#e8eaeb',fontSize:13,outline:'none',resize:'none',fontFamily:'inherit',boxSizing:'border-box'}}/>
      </div>

      <button onClick={submit} disabled={submitting||!form.niche||!form.audience}
        style={{width:'100%',padding:14,borderRadius:12,border:'none',background:'linear-gradient(135deg,#00c566,#00a854)',color:'#000',fontWeight:700,fontSize:15,cursor:'pointer',fontFamily:'inherit',opacity:submitting||!form.niche||!form.audience?.6:1}}>
        {submitting?'Submitting...':'Submit for Review'}
      </button>

      <div style={{fontSize:11,color:T.muted,textAlign:'center',marginTop:12}}>
        Review takes 24-48 hours. You'll be notified via Telegram.
      </div>
    </div>
  );
}
